import { Store } from "@reduxjs/toolkit";
import qs from "qs";
import { HttpHandler } from "./http-handler";

type Options = {
  method: string;
  contentType?: string;
  silent?: boolean;
};

type RequestExtras = {
  silent?: boolean;
};

type JSONobj =
  | {
    [key: string]: string | number | boolean | undefined | null | JSONobj | JSONobj[] | Blob;
  }
  | JSONobj[];

export class HttpRequest {
  store?: Store;
  host = "";
  errorHandler?: (statusCode: number, error: HttpHandler, api?: string) => void;

  constructor(host = "", errorHandler?: (statusCode: number, error: HttpHandler, api?: string) => void) {
    this.errorHandler = errorHandler;
    this.host = host;
  }

  async request(api: string, data: JSONobj, options: Options) {
    if (!this.store) {
      throw new Error("No store found");
    }

    const state = this.store.getState();

    const defaultOptions: RequestInit = {
      credentials: "include",
      method: options.method,
      headers: {},
    };

    if (state.auth?.accessToken) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        Authorization: `Bearer ${state.auth.accessToken}`,
      };
    }

    if (state.auth?.deviceToken) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        "X-Device": state.auth.deviceToken,
      };
    }

    // Add locale header
    const savedLocale = localStorage.getItem("dehub-locale");
    if (savedLocale) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        "Accept-Language": savedLocale,
      };
    }

    if (options.contentType === "multipart/form-data") {
      defaultOptions.body = data as unknown as BodyInit;
    } else {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      };
      defaultOptions.body = JSON.stringify(data) as BodyInit;
    }

    let queryString = "";

    if (options.method === "GET") {
      delete defaultOptions.body;
      queryString = `?${qs.stringify(data)}`;
    }

    try {
      const res = await fetch(`${this.host}${api}${queryString}`, defaultOptions);
      const http = new HttpHandler(res.status);
      return await http.handle(res, options.silent);
    } catch (ex) {
      if (ex instanceof HttpHandler) {
        if (this.errorHandler) {
          this.errorHandler(ex.statusCode, ex, api);
        }
        return null;
      } else {
        if (this.errorHandler) {
          this.errorHandler(
            0,
            new HttpHandler(0, {
              code: "NETWORK_ERROR",
              error: "NETWORK_ERROR",
              message: (ex as Error).message || "Unknown network error",
            }),
            api,
          );
        }
        return null;
      }
    }
  }

  get(api: string, data?: JSONobj, extras?: RequestExtras) {
    return this.request(api, data || {}, { method: "GET", silent: extras?.silent });
  }

  post(api: string, data?: JSONobj, extras?: RequestExtras) {
    return this.request(api, data || {}, { method: "POST", silent: extras?.silent });
  }

  put(api: string, data?: JSONobj, extras?: RequestExtras) {
    return this.request(api, data || {}, { method: "PUT", silent: extras?.silent });
  }

  del(api: string, data?: JSONobj, extras?: RequestExtras) {
    return this.request(api, data || {}, { method: "DELETE", silent: extras?.silent });
  }

  upload(api: string, data?: JSONobj, extras?: RequestExtras) {
    return this.request(api, data || {}, { method: "POST", contentType: "multipart/form-data", silent: extras?.silent });
  }
}
